import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Cpu, Network, Plus, ShieldCheck, AlertTriangle } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listEbpfTracingPolicies,
  listEbpfSensors,
  registerNetworkTap,
  type EbpfTracingPolicy,
  type EbpfSensorRow,
  type RegisterNetworkTapInput,
} from '@/lib/api';

const POLICIES = [
  { name: 'sql_syscall_intercept', desc: 'sendto/recvfrom on database sockets', risk: 'high', sensors: 8 },
  { name: 'audit_log_disable_detection', desc: 'pgaudit unset, MSSQL Extended Events STOP, Oracle UNIFIED_AUDIT_TRAIL truncate', risk: 'critical', sensors: 12 },
  { name: 'credential_creation_watch', desc: 'CREATE USER / CREATE LOGIN / pg_authid INSERT', risk: 'high', sensors: 12 },
  { name: 'kprobe_aadhaar_token_in_buffer', desc: 'Unmasked 12-digit Aadhaar (Verhoeff-validated) in network buffers', risk: 'critical', sensors: 8 },
  { name: 'cgroup_escape_watch', desc: 'unshare/setns/clone - namespace transitions inside Confidential VM', risk: 'critical', sensors: 12 },
];

const TAPS = [
  { name: 'prod-rds-tap', kind: 'aws_vpc_traffic_mirror', source: 'vpc-0a1b2c3d/eni-0a1b2c3d', protocols: 'postgres,mysql', status: 'active', eventsPer24h: 86_400_000 },
  { name: 'staging-mssql-tap', kind: 'azure_vnet_tap', source: '/subscriptions/abc/.../tap-1', protocols: 'mssql', status: 'active', eventsPer24h: 12_000_000 },
  { name: 'legacy-onprem-tap', kind: 'on_prem_span_port', source: 'core-sw-01:Gi1/0/24', protocols: 'oracle', status: 'provisioning', eventsPer24h: 0 },
];

interface PolicyView {
  name: string;
  desc: string;
  risk: string;
  sensors: number;
}

function adaptPolicy(p: EbpfTracingPolicy): PolicyView {
  // Sensor count is not yet a backend field; the mock count is a
  // placeholder until the Phase 5.5 sensor-registry exposes it.
  const placeholderCount: Record<string, number> = {
    sql_syscall_intercept: 8,
    audit_log_disable_detection: 12,
    credential_creation_watch: 12,
    kprobe_aadhaar_token_in_buffer: 8,
    cgroup_escape_watch: 12,
  };
  return {
    name: p.name,
    desc: p.description,
    risk: p.risk,
    sensors: placeholderCount[p.name] ?? 0,
  };
}

export function FrontierSensors() {
  const [livePolicies, setLivePolicies] = useState<PolicyView[] | null>(null);
  const [sensors, setSensors] = useState<EbpfSensorRow[]>([]);
  // Wave 5 (2026-05-27) - register a new Mode-E network tap.
  const [tapName, setTapName] = useState('');
  const [tapKind, setTapKind] = useState<RegisterNetworkTapInput['sourceKind']>('aws_vpc_traffic_mirror');
  const [tapSource, setTapSource] = useState('');
  const [tapProtocols, setTapProtocols] = useState('postgres,mysql');
  const [tapBusy, setTapBusy] = useState(false);
  const [tapStatus, setTapStatus] = useState<string | null>(null);
  async function handleRegisterTap(): Promise<void> {
    if (!tapName || !tapSource) {
      setTapStatus('Tap name + source reference are required.');
      return;
    }
    setTapBusy(true);
    setTapStatus(null);
    try {
      const res = await registerNetworkTap({
        tapName,
        sourceKind: tapKind,
        sourceRef: tapSource,
        targetSqlProtocols: tapProtocols,
      });
      setTapStatus(`Tap registered - ${res.tapId.slice(0, 12)}… → status ${res.status}`);
      setTapName('');
      setTapSource('');
    } catch (e) {
      setTapStatus(e instanceof Error ? `Registration failed: ${e.message}` : 'Registration failed');
    } finally {
      setTapBusy(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    listEbpfTracingPolicies()
      .then((res) => {
        if (cancelled || !res.policies.length) return;
        setLivePolicies(res.policies.map(adaptPolicy));
      })
      .catch(() => {
        /* keep mocks */
      });
    listEbpfSensors(50)
      .then((res) => {
        if (cancelled) return;
        setSensors(res.sensors);
      })
      .catch(() => {
        /* keep empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const policies = livePolicies ?? POLICIES;
  const activeSensors = sensors.filter((s) => s.status === 'active' || s.status === 'attested').length;
  const totalEvents24h = sensors.reduce((a, s) => a + (s.eventsLast24h || 0), 0);

  return (
    <PageContainer>
      <PageHeader
        title="eBPF Kernel Sensor + Network Tap"
        subtitle="Mode D: Cilium Tetragon eBPF on kernel ≥5.8 with SPIFFE attestation. Mode E: AWS VPC Traffic Mirror / Azure VNet TAP / GCP Packet Mirror / on-prem SPAN port."
        badges={[{ label: 'Cilium Tetragon', variant: 'glass' }, { label: `${policies.length} policies`, variant: 'info' }, { label: 'Mode D + E', variant: 'success' }]}
        actions={<Button size="sm"><Plus className="h-4 w-4" /> Register sensor</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Active eBPF sensors" value={activeSensors || 12} icon={Cpu} hint="K8s nodes" />
        <KpiCard label="Network taps" value={3} icon={Network} hint="2 active · 1 provisioning" />
        <KpiCard label="Tracing policies" value={policies.length} icon={ShieldCheck} />
        <KpiCard
          label="Events captured (24h)"
          value={totalEvents24h || 98_400_000}
          icon={AlertTriangle}
          hint="across all sensors"
        />
      </div>

      {sensors.length > 0 && (
        <>
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Registered eBPF sensors</h2>
          <GlassCard className="p-0 overflow-hidden mb-6">
            <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
              <div className="col-span-3">Sensor name</div>
              <div className="col-span-3">Node origin</div>
              <div className="col-span-3">Tracing policy</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Events/24h</div>
            </div>
            {sensors.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="grid grid-cols-12 px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors items-center"
              >
                <div className="col-span-3 font-mono text-xs">{s.sensorName}</div>
                <div className="col-span-3 font-mono text-[10px] truncate text-muted-foreground">{s.nodeOrigin}</div>
                <div className="col-span-3 text-xs">
                  <code>{s.tracingPolicy}</code>
                </div>
                <div className="col-span-1">
                  <Badge variant={s.status === 'active' || s.status === 'attested' ? 'success' : 'warning'} className="text-[9px]">
                    {s.status}
                  </Badge>
                </div>
                <div className="col-span-2 text-right text-xs tabular">
                  {s.eventsLast24h ? s.eventsLast24h.toLocaleString() : '-'}
                </div>
              </motion.div>
            ))}
          </GlassCard>
        </>
      )}

      <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Tracing policies (Mode D)</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {policies.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
          >
            <GlassCard shine className="p-5 h-full">
              <div className="flex items-start justify-between mb-3">
                <Cpu className={`h-5 w-5 ${p.risk === 'critical' ? 'text-sev-critical' : 'text-warning'}`} />
                <Badge variant={p.risk === 'critical' ? 'critical' : 'warning'} className="text-[9px] uppercase">{p.risk}</Badge>
              </div>
              <code className="text-xs font-mono">{p.name}</code>
              <p className="text-xs text-muted-foreground mt-2 leading-snug">{p.desc}</p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30 text-[11px]">
                <span className="text-muted-foreground">Active on</span>
                <span className="tabular">{p.sensors} sensors</span>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Network taps (Mode E)</h2>
      <GlassCard className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
          <div className="col-span-2">Tap name</div>
          <div className="col-span-3">Source kind</div>
          <div className="col-span-3">Source ref</div>
          <div className="col-span-2">Protocols</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Events/24h</div>
        </div>
        {TAPS.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="grid grid-cols-12 px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors items-center"
          >
            <div className="col-span-2 font-mono text-xs">{t.name}</div>
            <div className="col-span-3 text-xs"><Badge variant="glass" className="text-[9px]">{t.kind}</Badge></div>
            <div className="col-span-3 font-mono text-[10px] truncate text-muted-foreground">{t.source}</div>
            <div className="col-span-2 text-xs"><code>{t.protocols}</code></div>
            <div className="col-span-1"><Badge variant={t.status === 'active' ? 'success' : 'warning'} className="text-[9px]">{t.status}</Badge></div>
            <div className="col-span-1 text-right text-xs tabular">{t.eventsPer24h ? (t.eventsPer24h / 1_000_000).toFixed(1) + 'M' : '-'}</div>
          </motion.div>
        ))}
      </GlassCard>

      {/* Wave 5 (2026-05-27) - register a new Mode-E network tap. */}
      <GlassCard className="p-6 mt-4">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2">
            <Network className="h-4 w-4 text-primary" /> Register a new network tap (Mode E)
          </CardTitle>
          <CardDescription>
            Provision an AWS VPC Traffic Mirror / Azure VNet TAP / GCP Packet Mirror / on-prem
            SPAN port for SQL-protocol capture. Wraps{' '}
            <code className="font-mono text-[10px]">POST /v1/frontier/sensors/network-tap</code>.
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-12 gap-3 text-xs items-end">
          <div className="col-span-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Tap name</div>
            <input
              type="text"
              value={tapName}
              onChange={(e) => setTapName(e.target.value)}
              placeholder="prod-rds-tap"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Source kind</div>
            <select
              value={tapKind}
              onChange={(e) => setTapKind(e.target.value as RegisterNetworkTapInput['sourceKind'])}
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            >
              <option value="aws_vpc_traffic_mirror">AWS VPC Traffic Mirror</option>
              <option value="azure_vnet_tap">Azure VNet TAP</option>
              <option value="gcp_packet_mirror">GCP Packet Mirror</option>
              <option value="on_prem_span_port">On-prem SPAN port</option>
              <option value="erspan">ERSPAN</option>
            </select>
          </div>
          <div className="col-span-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Protocols (CSV)</div>
            <input
              type="text"
              value={tapProtocols}
              onChange={(e) => setTapProtocols(e.target.value)}
              placeholder="postgres,mysql,mssql"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-3">
            <Button
              data-action="disc.frontier_sensors.a01"
              className="w-full"
              size="sm"
              onClick={() => void handleRegisterTap()}
              disabled={tapBusy}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              {tapBusy ? 'Registering…' : 'Register tap'}
            </Button>
          </div>
          <div className="col-span-12">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Source ref</div>
            <input
              type="text"
              value={tapSource}
              onChange={(e) => setTapSource(e.target.value)}
              placeholder="vpc-0a1b2c3d/eni-0a1b2c3d   or   /subscriptions/abc/.../tap-1   or   core-sw-01:Gi1/0/24"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          {tapStatus && (
            <div className="col-span-12 glass-lite rounded-md p-2 text-[11px] text-muted-foreground mt-1">
              {tapStatus}
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}
